import React from 'react';
import moment from 'moment';

import { CfdiIndexEntry, CFDI_STATUS_LABEL } from '../../models/CfdiIndex';
import { buildCfdiDownloadUrl } from '../../services/fiscalpopApi';
import { formatCurrencyStyled } from '../visuals/currency.formatted';
import ItemMenu from '../Forms/menu/MenuComponent';
import { entryRoleLabel, entryTypeLabel, shortUuid } from './facturas.actions';

export interface EntryActions {
    onOpenGroup: (entry: CfdiIndexEntry) => void;
    onAskCancel: (entry: CfdiIndexEntry) => void;
    onAskEdit: (entry: CfdiIndexEntry) => void;
    onCartaPortePreview: (entry: CfdiIndexEntry) => void;
    onFilterUuid: (uuid: string) => void;
}

interface RowProps extends EntryActions {
    entry: CfdiIndexEntry;
    authToken: string;
    busyUuid: string;
    setUnsetTransform: (bool: boolean) => void;
}

export const canCancelEntry = (entry: CfdiIndexEntry) => entry.status === 'vigente' && !!entry.argumentId;
export const canEditEntry = (entry: CfdiIndexEntry) => entry.status === 'vigente' && !!entry.argumentId && entry.kind !== 'orphan';

export const relatedWarning = (entry: CfdiIndexEntry): string | null => {
    if (!entry.relatedCfdis) return null;
    const n = entry.relatedCfdis;
    return `Tiene ${n} CFDI${n === 1 ? '' : 's'} relacionado${n === 1 ? '' : 's'} (pagos, notas de crédito o sustituciones). \nEl SAT podría rechazar su cancelación o edición.`;
};

export function StatusBadge({ entry, onFilterUuid }: { entry: CfdiIndexEntry; onFilterUuid?: (uuid: string) => void }) {
    const label = CFDI_STATUS_LABEL[entry.status] || entry.status;
    const warning = entry.status === 'vigente' ? relatedWarning(entry) : null;
    return (
        <div className={`_status _${entry.status}`}>
            <span className="_badge">{label}</span>
            {
                warning ?
                    <span className="_related" title={warning}>
                        <i className="material-icons">link</i>
                        Relacionada
                    </span>
                    : null
            }
            {
                entry.status === 'sustituida' && entry.replacedByUuid ?
                    <button type="button" className="_link" title={`Ver factura sustituta ${entry.replacedByUuid}`} onClick={() => onFilterUuid && onFilterUuid(entry.replacedByUuid)}>
                        por {shortUuid(entry.replacedByUuid)}
                    </button>
                    : null
            }
            {
                entry.replacesUuid && entry.status !== 'sustituida' ?
                    <button type="button" className="_link" title={`Sustituye a ${entry.replacesUuid}`} onClick={() => onFilterUuid && onFilterUuid(entry.replacesUuid)}>
                        sustituye {shortUuid(entry.replacesUuid)}
                    </button>
                    : null
            }
        </div>
    );
}

function FacturasRow({ entry, authToken, busyUuid, setUnsetTransform, onOpenGroup, onAskCancel, onAskEdit, onCartaPortePreview, onFilterUuid }: RowProps) {
    const isCartaPorte = entry.specialType === 'cartaporte';
    const isBusy = busyUuid === entry.uuid;
    const cancelable = canCancelEntry(entry) && !busyUuid;
    const editable = canEditEntry(entry) && !busyUuid;
    const hasGroup = !!entry.argumentId;
    const role = entryRoleLabel(entry);
    const pdfUrl = buildCfdiDownloadUrl(authToken, entry.uuid, 'pdf');
    const xmlUrl = buildCfdiDownloadUrl(authToken, entry.uuid, 'xml');

    const copyUuid = () => {
        if (navigator.clipboard) navigator.clipboard.writeText(entry.uuid).catch(() => { });
    };

    return (
        <div className={`_entry ${isBusy ? '_busy' : ''} _st_${entry.status}`}>
            <div className="_type">
                <p className={`_tipo _mp${entry.multipayType ?? 0}`}>{entryTypeLabel(entry)}</p>
                {
                    hasGroup ?
                        <button type="button" className="_role _link" onClick={() => onOpenGroup(entry)} title="Ver facturas relacionadas">
                            {role || 'Ver relación'}
                        </button>
                        : <p className="_role _muted">{role}</p>
                }
            </div>
            <div className="_client">
                <p className="_name">{entry.receptorNombre}</p>
                <p className="_rfc">{entry.receptorRfc}</p>
            </div>
            <div className="_serie">
                <p className="_folio"><b>Serie:</b> {entry.serie || '—'} <b>Folio:</b> {entry.folio || '—'}</p>
                <p className="_uuid" title="Copiar UUID" onClick={copyUuid}>{entry.uuid}</p>
            </div>
            <div className="_fecha">
                <p>{moment(entry.created).format('DD MMM YYYY')}</p>
                <p className="_hour">{moment(entry.created).format('HH:mm')}</p>
            </div>
            <StatusBadge entry={entry} onFilterUuid={onFilterUuid} />
            <div className="_totals">
                <p>{formatCurrencyStyled(entry.total || 0)}</p>
            </div>

            <button className="svg yellow" title="Editar factura" onClick={() => onAskEdit(entry)} disabled={!editable}>
                <i className="material-icons">create</i>
            </button>
            {
                isCartaPorte ?
                    <button className="svg violet" title="Ver Carta Porte" onClick={() => onCartaPortePreview(entry)}>
                        <i className="material-icons">visibility</i>
                    </button>
                    :
                    <a className="svg violet" title="Descargar PDF" rel="noopener noreferrer" href={pdfUrl} download={true}>
                        <i className="material-icons">picture_as_pdf</i>
                    </a>
            }
            <button className="svg red" title="Cancelar factura" onClick={() => onAskCancel(entry)} disabled={!cancelable}>
                <i className="material-icons">delete</i>
            </button>
            <ItemMenu triggerContent={
                <button className="svg violet" title="Opciones">
                    <i className="material-icons">more_vert</i>
                </button>
            } noPadding={true} overlayRootElement={'FacturasBody'} onOpenChange={setUnsetTransform}>
                <a title="Descargar PDF" rel="noopener noreferrer" href={pdfUrl} download={true}>
                    <div className="_option">
                        <i className="material-icons">picture_as_pdf</i>
                        <span>Descargar PDF</span>
                    </div>
                </a>
                <a title="Descargar XML" rel="noopener noreferrer" href={xmlUrl} download={true}>
                    <div className="_option">
                        <i className="material-icons">code</i>
                        <span>Descargar XML</span>
                    </div>
                </a>
                {
                    isCartaPorte ?
                        <div className="_option" onClick={() => onCartaPortePreview(entry)}>
                            <i className="material-icons">visibility</i>
                            <span>Ver Carta Porte</span>
                        </div>
                        : null
                }
                {
                    hasGroup ?
                        <div className="_option" onClick={() => onOpenGroup(entry)}>
                            <i className="material-icons">account_tree</i>
                            <span>Ver relación</span>
                        </div>
                        : null
                }
                <div className={`_option ${editable ? '' : '_disabled'}`} onClick={editable ? () => onAskEdit(entry) : undefined}>
                    <i className="material-icons">create</i>
                    <span>Editar factura</span>
                </div>
                <div className={`_option ${cancelable ? 'red' : '_disabled'}`} onClick={cancelable ? () => onAskCancel(entry) : undefined}>
                    <i className="material-icons">delete</i>
                    <span>{isBusy ? 'Cancelando…' : 'Cancelar factura'}</span>
                </div>
            </ItemMenu>
        </div>
    );
}

export default FacturasRow;
